import { useEffect, useState } from 'react';
import axios from '../api/axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, CircularProgress,
  Select, InputLabel, FormControl, MenuItem
} from '@mui/material';
import Layout from '../components/Layout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { red } from '@mui/material/colors';
import { LoadingButton } from '@mui/lab';

export default function Products() {
  const user_id = localStorage.getItem("user_id");
  const [productFormRows, setProductFormRows] = useState([]);
  const [productCatalogFormRows, setProductCatalogFormRows] = useState([]);
  const [productFormOpen, setProductFormOpen] = useState(false);
  const [productCatalogFormOpen, setProductCatalogFormOpen] = useState(false);
  const [productFormEditingId, setProductFormEditingId] = useState(null);
  const [productCatalogFormEditingId, setProductCatalogFormEditingId] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', remark: '', product_catalog_id: '' });
  const [productImageFile, setProductImageFile] = useState(null);
  const [productImagePreview, setProductImagePreview] = useState('');
  const [productCatalogForm, setProductCatalogForm] = useState({ name: '', remark: '' });
  const [productFormloading, setProductFormLoading] = useState(false);
  const [productCatalogFormloading, setProductCatalogFormLoading] = useState(false);
  const [productFormConfirmDeleteId, setProductFormConfirmDeleteId] = useState(null);
  const [productCatalogFormConfirmDeleteId, setProductCatalogFormConfirmDeleteId] = useState(null);
  const [submittingProduct, setSubmittingProduct] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [submittingCatalog, setSubmittingCatalog] = useState(false);
  const [deletingCatalog, setDeletingCatalog] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false); // (optional if you re-enable upload button)
  const [productFormError, setProductFormError] = useState('');
  const [productCatalogFormError, setProductCatalogFormError] = useState('');

  const [productFormSearchText, setProductFormSearchText] = useState('');
  const [productCatalogFormSearchText, setProductCatalogFormSearchText] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchProductCatalog();
  }, []);

  const fetchProducts = async () => {
    const params = {};
    if (productFormSearchText) params.search = productFormSearchText;
    await runFetchProducts(params);
  };

  const runFetchProducts = async (params = {}) => {
    setProductFormLoading(true);
    try {
      const res = await axios.get('/products', {
        params: { search: params.search }
      });
      const formatted = res.data.map((p) => ({
        ...p,
        id: p.id,
        created_at: p.created_at ? dayjs(p.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
        updated_at: p.updated_at ? dayjs(p.updated_at).format('YYYY-MM-DD HH:mm:ss') : ''
      }));
      setProductFormRows(formatted);
    } finally {
      setProductFormLoading(false);
    }
  }

  const resetProductsFilter = () => {
    // 1. Clear the UI
    setProductFormSearchText('');
    // setFilterType('');
    // setFilterSite('');
    // setFilterStartDate(null);
    // setFilterEndDate(null);

    // 2. Force a fresh fetch with no filters (not using stale state)
    runFetchProducts({});
  };

  const fetchProductCatalog = async () => {
    const params = {};
    if (productCatalogFormSearchText) params.search = productCatalogFormSearchText;
    await runFetchProductCatalog(params);
  }

  const runFetchProductCatalog = async (params = {}) => {
    setProductCatalogFormLoading(true);
    try {
      const res = await axios.get('/productCatalog', {
        params: { search: params.search }
      });
      const formatted = res.data.map((p) => ({
        ...p,
        id: p.id,
        created_at: p.created_at ? dayjs(p.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
        updated_at: p.updated_at ? dayjs(p.updated_at).format('YYYY-MM-DD HH:mm:ss') : ''
      }));
      setProductCatalogFormRows(formatted);
    } finally {
      setProductCatalogFormLoading(false);
    }
  }

  const resetProductCatalogFilter = () => {
    // 1. Clear the UI
    setProductCatalogFormSearchText('');
    // setFilterType('');
    // setFilterSite('');
    // setFilterStartDate(null);
    // setFilterEndDate(null);

    // 2. Force a fresh fetch with no filters (not using stale state)
    runFetchProductCatalog({});
  };

  const handleProductFormOpen = () => {
    setProductFormEditingId(null);
    // setProductForm({ name: '', remark: '', product_catalog_id: '' });
    setProductForm({
      name: '',
      remark: '',
      // product_catalog_id: productCatalogFormRows[0]?.id ?? '',
      product_catalog_id: '',
    });
    setProductImageFile(null);
    setProductImagePreview('');
    setProductFormOpen(true);
    setProductFormError('');
  };

  const handleProductFormClose = () => {
    setProductFormOpen(false);
    setProductForm({ name: '', remark: '', product_catalog_id: '' });
    setProductImageFile(null);
    setProductImagePreview('');
  };

  const handleProductFormEdit = (row) => {
    setProductForm({
      name: row.name,
      remark: row.remark,
      product_catalog_id: row.product_catalog_id ?? ''
    });
    setProductFormEditingId(row.id);
    setProductImageFile(null);
    setProductImagePreview(row.photo_url || ''); // if your API returns photo_url
    setProductFormOpen(true);
    setProductFormError('');
  };

  const handleProductCatalogFormOpen = () => {
    setProductCatalogFormEditingId(null);
    setProductCatalogForm({ name: '', remark: '', product_catalog_id: '' });
    setProductCatalogFormOpen(true);
    setProductCatalogFormError('');
  };

  const handleProductCatalogFormClose = () => {
    setProductCatalogFormOpen(false);
    setProductCatalogForm({ name: '', remark: '', product_catalog_id: '' });
  };

  const handleProductCatalogFormEdit = (row) => {
    setProductCatalogForm({ name: row.name, remark: row.remark });
    setProductCatalogFormEditingId(row.id);
    setProductCatalogFormOpen(true);
    setProductCatalogFormError('');
  };

  const handleProductImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProductImageFile(file);
    setProductImagePreview(URL.createObjectURL(file));
  };

  // const handleUploadProductPhoto = async () => {
  //   if (!productFormEditingId || !productImageFile) return;
  //   const formData = new FormData();
  //   formData.append('photo', productImageFile);
  //   try {
  //     await axios.post(`/products/${productFormEditingId}/photo`, formData);
  //     await fetchProducts();
  //     alert('照片已上傳');
  //   } catch (err) {
  //     alert('上傳照片失敗');
  //   }
  // };

  const validateProductForm = () => {
    if (!productForm.name) return '請輸入貨物名稱';
    if (!productForm.product_catalog_id) return '請選擇貨種';
  }

  const handleProductFormSubmit = async () => {
    const v = validateProductForm();
    if (v) { setProductFormError(v); return; }

    try {
      setSubmittingProduct(true);
      const formData = new FormData();
      formData.append('name', productForm.name || '');
      formData.append('remark', productForm.remark || '');
      formData.append('product_catalog_id', productForm.product_catalog_id || '');

      // If editing and there is a new image, send it together
      if (productImageFile) {
        formData.append('photo', productImageFile);
      }

      if (productFormEditingId) {
        // Update product + photo in same call
        await axios.put(`/products/${productFormEditingId}`, formData);
      } else {
        await axios.post(`/products`, formData);
      }

      handleProductFormClose();
      fetchProducts();
      fetchProductCatalog();
    } catch (error) {
      if (error.response.data.message === "Product already exists") {
        if (productFormEditingId) {
          setProductFormError('無法更改，此貨物已註冊');
        } else {
          setProductFormError('無法新增，此貨物已註冊');
        }
      } else {
        alert('Failed to submit product');
      }
    } finally {
      setSubmittingProduct(false);
    }
  };

  const validateProductCatalogForm = () => {
    if (!productCatalogForm.name) return '請輸入貨種名稱';
  }

  const handleProductCatalogFormSubmit = async () => {
    const v = validateProductCatalogForm();
    if (v) { setProductCatalogFormError(v); return; }

    try {
      setSubmittingCatalog(true);
      if (productCatalogFormEditingId) {
        await axios.put(`/productCatalog/${productCatalogFormEditingId}`, productCatalogForm);
      } else {
        await axios.post('/productCatalog', productCatalogForm);
      }
      handleProductCatalogFormClose();
      fetchProducts();
      fetchProductCatalog();
    } catch (error) {
      if (error.response.data.message === "Product Catalog already exists") {
        if (productCatalogFormEditingId) {
          setProductCatalogFormError('無法更改，此貨種已註冊');  
        } else {
          setProductCatalogFormError('無法新增，此貨種已註冊');
        }
      } else {
        alert('Failed to submit product catalog');
      }
    } finally {
      setSubmittingCatalog(false);
    }
  };


  const handleProductFormDelete = (id) => {
    setProductFormConfirmDeleteId(id);
  };

  const handleProductCatalogFormDelete = (id) => {
    setProductCatalogFormConfirmDeleteId(id);
  };

  const confirmProductFormDelete = async () => {
    if (!productFormConfirmDeleteId) return;
    try {
      setProductFormLoading(true);
      setDeletingProduct(true);

      await axios.put(`/products/${productFormConfirmDeleteId}`, { active: false });
      setProductFormConfirmDeleteId(null);
      fetchProducts();
      fetchProductCatalog();
    } catch (error) {
      alert('刪除產品失敗');
    } finally {
      setProductFormLoading(false);
      setDeletingProduct(false);
    }
  };

  const confirmProductCatalogFormDelete = async () => {
    if (!productCatalogFormConfirmDeleteId) return;
    try {
      setDeletingCatalog(true);
      setProductCatalogFormLoading(true);
      await axios.put(`/productCatalog/${productCatalogFormConfirmDeleteId}`, { active: false });
      setProductCatalogFormConfirmDeleteId(null);
      fetchProductCatalog();
    } catch (error) {
      alert('刪除產品失敗');
    } finally {
      setDeletingCatalog(false);
      setProductCatalogFormLoading(false);
    }
  }

  const productFormColumns = [
    {
      field: 'photo_url',
      headerName: '圖片',
      width: 100,
      renderCell: (params) => {
        const url = params.row.photo_url;
        return url ? (
          <a href={url} target="_blank">
            <img
              src={url}
              alt="product"
              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px' }}
            />
          </a>
        ) : null;
      }
    },
    { field: 'name', headerName: '貨物名稱', width: 120 },
    { field: 'product_catalog_name', headerName: '貨種', width: 120 },
    { field: 'total_amount', headerName: '總數', width: 100 },
    {
      field: 'site_name',
      headerName: '地盤',
      width: 180,
      renderCell: (params) => {
        const items = params?.row?.site_status || [];
        return (
          <div className="site_inventory_summary" style={{ whiteSpace: 'normal' }}>
            {items.map((item, index) => (
              <div key={index}>
                {item.site_name && item.amount != 0 ? item.site_name : ""}
              </div>
            ))}
          </div>
        );
      }
    },
    {
      field: 'site_contain_amounts',
      headerName: '數量',
      width: 100,
      renderCell: (params) => {
        const items = params?.row?.site_status || [];
        return (
          <div className="site_inventory_summary" style={{ whiteSpace: 'normal' }}>
            {items.map((item, index) => (
              <div key={index}>
                {item.site_name && item.amount != 0 ? item.amount : ""}
              </div>
            ))}
          </div>
        );
      }
    },
    { field: 'remark', headerName: '備註', width: 300 },
    { field: 'created_at', headerName: '新增時間', width: 180 },
    { field: 'updated_at', headerName: '最後修改時間', width: 180 },
    {
      field: 'actions',
      headerName: '動作',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleProductFormEdit(params.row)}><EditIcon /></IconButton>
          {user_id && user_id <= 3 && (
            <IconButton onClick={() => handleProductFormDelete(params.row.id)} sx={{ color: red[800] }}><DeleteIcon /></IconButton>
          )}
        </>
      )
    }
  ];

  const productCatalogFormColumns = [
    { field: 'name', headerName: '貨種名稱', width: 200 },
    {
      field: 'product',
      headerName: '貨物名單',
      width: 200,
      renderCell: (params) => {
        const items = params?.row?.product || [];
        return (
          <div className="site_inventory_summary" style={{ whiteSpace: 'normal' }}>
            {items.map((item, index) => (
              <div key={index}>
                {item}
              </div>
            ))}
          </div>
        );
      }
    },
    { field: 'remark', headerName: '備註', width: 700 },
    { field: 'created_at', headerName: '新增時間', width: 180 },
    { field: 'updated_at', headerName: '最後修改時間', width: 180 },
    {
      field: 'actions',
      headerName: '動作',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleProductCatalogFormEdit(params.row)}><EditIcon /></IconButton>
          {user_id && user_id <= 3 && (
            <IconButton onClick={() => handleProductCatalogFormDelete(params.row.id)} sx={{ color: red[800] }}><DeleteIcon /></IconButton>
          )}
        </>
      )
    }
  ];

  return (
    <Layout>
      <Typography variant="h5" mb={2} sx={{ paddingLeft: "8px" }}>貨物</Typography>
      <Box className="filter-container" sx={{ display: 'flex', gap: 1, paddingLeft: "8px", mb: 2 }}>
        <Button className="new-item-button" variant="contained" onClick={handleProductFormOpen}>
          新增貨物
        </Button>
        <TextField
          variant="outlined"
          size="small"
          placeholder="搜尋貨物名稱或備註"
          value={productFormSearchText}
          onChange={(e) => setProductFormSearchText(e.target.value)}
        />
        <Button variant="contained" onClick={fetchProducts}>搜尋</Button>
        <Button variant="contained" onClick={resetProductsFilter}>重設</Button>
      </Box>

      {productFormloading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
          <DataGrid
            rows={productFormRows}
            columns={productFormColumns}
            pageSize={10}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            disableColumnMenu={true}
          />
        </Box>
      )}

      <Typography variant="h5" mb={2} sx={{ paddingLeft: "8px" }}>貨種</Typography>
      <Box className="filter-container" sx={{ display: 'flex', gap: 1, paddingLeft: "8px", mb: 2 }}>
        <Button variant="contained" onClick={handleProductCatalogFormOpen}>
          新增貨種
        </Button>
        <TextField
          variant="outlined"
          size="small"
          placeholder="搜尋貨種名稱或備註"
          value={productCatalogFormSearchText}
          onChange={(e) => setProductCatalogFormSearchText(e.target.value)}
        />
        <Button variant="contained" onClick={fetchProductCatalog}>搜尋</Button>
        <Button variant="contained" onClick={resetProductCatalogFilter}>重設</Button>
      </Box>
      {productCatalogFormloading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
          <DataGrid
            rows={productCatalogFormRows}
            columns={productCatalogFormColumns}
            pageSize={10}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            disableColumnMenu={true}
          />
        </Box>
      )}

      <Dialog open={productFormOpen} onClose={handleProductFormClose}>
        <DialogTitle>{productFormEditingId ? '修改貨物' : '新增貨物'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="貨物名稱*"
            value={productForm.name}
            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
            margin="dense"
          />

          <FormControl sx={{ minWidth: "150", width: "100%" }}>
            <InputLabel>貨種*</InputLabel>
            <Select
              label="貨種"
              value={productForm.product_catalog_id}
              onChange={(e) =>
                setProductForm({
                  ...productForm,
                  product_catalog_id: e.target.value === '' ? '' : Number(e.target.value),
                })
              }
            >
              {productCatalogFormRows.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="備註"
            value={productForm.remark}
            onChange={(e) => setProductForm({ ...productForm, remark: e.target.value })}
            margin="dense"
          />
          {productFormError && (
            <Typography variant="body2" color="error" sx={{ ml: 1 }}>{productFormError}</Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" component="label">
              選擇照片
              <input
                hidden
                accept="image/*"
                type="file"
                onChange={handleProductImageChange}
              />
            </Button>

            {productImagePreview && (
              <Box sx={{ mt: 1 }}>
                <img
                  src={productImagePreview}
                  alt="產品預覽"
                  style={{ maxWidth: 240, maxHeight: 240, display: 'block', borderRadius: 8 }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProductFormClose} disabled={submittingProduct}>取消</Button>
          <LoadingButton
            onClick={handleProductFormSubmit}
            variant="contained"
            loading={submittingProduct}
          >
            {productFormEditingId ? '更改' : '新增'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!productFormConfirmDeleteId} onClose={() => setProductFormConfirmDeleteId(null)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          您確定要刪除此貨物嗎？
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductFormConfirmDeleteId(null)} disabled={deletingProduct}>取消</Button>
          <LoadingButton
            onClick={confirmProductFormDelete}
            color="error"
            variant="contained"
            loading={deletingProduct}
          >
            刪除
          </LoadingButton>
        </DialogActions>
      </Dialog>


      <Dialog open={productCatalogFormOpen} onClose={handleProductCatalogFormClose}>
        <DialogTitle>{productCatalogFormEditingId ? '修改貨種' : '新增貨種'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="貨種名稱*"
            value={productCatalogForm.name}
            onChange={(e) => setProductCatalogForm({ ...productCatalogForm, name: e.target.value })}
            margin="dense"
          />
          <TextField
            fullWidth
            label="備註"
            value={productCatalogForm.remark}
            onChange={(e) => setProductCatalogForm({ ...productCatalogForm, remark: e.target.value })}
            margin="dense"
          />
          {productCatalogFormError && (
            <Typography variant="body2" color="error" sx={{ ml: 1 }}>{productCatalogFormError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProductCatalogFormClose} disabled={submittingCatalog}>取消</Button>
          <LoadingButton
            onClick={handleProductCatalogFormSubmit}
            variant="contained"
            loading={submittingCatalog}
          >
            {productCatalogFormEditingId ? '更改' : '新增'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!productCatalogFormConfirmDeleteId} onClose={() => setProductCatalogFormConfirmDeleteId(null)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          您確定要刪除此貨種嗎？
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductCatalogFormConfirmDeleteId(null)} disabled={deletingCatalog}>取消</Button>
          <LoadingButton
            onClick={confirmProductCatalogFormDelete}
            color="error"
            variant="contained"
            loading={deletingCatalog}
          >
            刪除
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
